import React, { useState, useMemo } from 'react'
import type { ResourceWeekViewProps, CalendarEvent } from '../types'
import { AppointmentCard } from '../components/AppointmentCard'
import { TimeLabels } from '../components/TimeLabels'
import { CurrentTimeIndicator } from '../components/CurrentTimeIndicator'
import { getWeekDays, isToday, getEventsForDay } from '../utils/time'
import {
  calculateOverlapLayout,
  getOverlapEventStyle,
  DEFAULT_SLOT_HEIGHT,
  generateTimeSlots,
} from '../utils'
import { getDayNameShort } from '../utils/locale'

export const ResourceWeekView: React.FC<ResourceWeekViewProps> = ({
  weekStart,
  events,
  resources,
  grouping = 'by-resource',
  slotDuration,
  startHour,
  endHour,
  editable,
  locale,
  selectedEventIds = [],
  onEventClick,
  onSlotClick,
  onEventDrop,
}) => {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null)

  const slotHeight = DEFAULT_SLOT_HEIGHT
  const totalSlots = ((endHour - startHour) * 60) / slotDuration
  const gridHeight = totalSlots * slotHeight
  const slots = generateTimeSlots(startHour, endHour, slotDuration)
  const weekDays = getWeekDays(weekStart)

  const handleDragStart = (event: CalendarEvent) => (e: React.DragEvent) => {
    setDraggedEvent(event)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDrop = (
    e: React.DragEvent,
    day: Date,
    hour: number,
    minute: number,
    resourceId: string
  ) => {
    e.preventDefault()
    if (draggedEvent && editable) {
      const newStart = new Date(day)
      newStart.setHours(hour, minute, 0, 0)
      onEventDrop?.(draggedEvent, newStart, resourceId)
      setDraggedEvent(null)
    }
  }

  const handleSlotClick = (
    day: Date,
    hour: number,
    minute: number,
    resourceId: string
  ) => {
    if (editable) {
      onSlotClick?.(day, hour, minute, resourceId)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  // Group by Resource: Resources are primary columns, days are sub-columns
  if (grouping === 'by-resource') {
    return (
      <div className="calendar-resource-week-view">
        {/* Headers */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}
        >
          {/* Empty corner */}
          <div style={{ width: '60px', flexShrink: 0 }} />

          {/* Resource headers */}
          {resources.map((resource) => (
            <div
              key={resource.id}
              style={{
                flex: 1,
                borderLeft: '1px solid #e5e7eb',
              }}
            >
              {/* Resource name */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '8px',
                  borderBottom: '1px solid #e5e7eb',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                {resource.title} {resource.name}
              </div>

              {/* Day sub-headers */}
              <div style={{ display: 'flex' }}>
                {weekDays.map((day) => {
                  const dayIsToday = isToday(day)

                  return (
                    <div
                      key={day.toISOString()}
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '6px 2px',
                        backgroundColor: dayIsToday ? '#fef3c7' : undefined,
                        borderLeft: '1px dashed #e5e7eb',
                        fontSize: '11px',
                      }}
                    >
                      <div style={{ color: '#6b7280' }}>
                        {getDayNameShort(day.getDay(), locale)}
                      </div>
                      <div style={{ fontWeight: dayIsToday ? 700 : 500 }}>
                        {day.getDate()}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Grid */}
        <div
          style={{
            display: 'flex',
            maxHeight: '600px',
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          <TimeLabels
            startHour={startHour}
            endHour={endHour}
            slotDuration={slotDuration}
            slotHeight={slotHeight}
          />

          {/* Resource columns */}
          <div style={{ display: 'flex', flex: 1 }}>
            {resources.map((resource) => (
              <div
                key={resource.id}
                style={{
                  flex: 1,
                  display: 'flex',
                  borderLeft: '1px solid #e5e7eb',
                }}
              >
                {/* Day columns within resource */}
                {weekDays.map((day) => {
                  const dayEvents = getEventsForDay(events, day).filter(
                    (e) => e.doctor.id === resource.id
                  )
                  const eventLayouts = useMemo(
                    () =>
                      calculateOverlapLayout(dayEvents, startHour, slotDuration, slotHeight),
                    [dayEvents]
                  )
                  const dayIsToday = isToday(day)

                  return (
                    <div
                      key={day.toISOString()}
                      style={{
                        flex: 1,
                        position: 'relative',
                        borderLeft: '1px dashed #e5e7eb',
                        backgroundColor: dayIsToday ? '#fffef0' : undefined,
                        minHeight: gridHeight,
                      }}
                    >
                      {/* Slots */}
                      {slots.map((slot) => {
                        const isHourStart = slot.minute === 0

                        return (
                          <div
                            key={`${day.toISOString()}-${slot.hour}-${slot.minute}`}
                            style={{
                              height: slotHeight,
                              borderBottom: isHourStart
                                ? '1px solid #d1d5db'
                                : '1px dashed #e5e7eb',
                              cursor: editable ? 'pointer' : 'default',
                              boxSizing: 'border-box',
                            }}
                            onClick={() =>
                              handleSlotClick(day, slot.hour, slot.minute, resource.id)
                            }
                            onDragOver={handleDragOver}
                            onDrop={(e) =>
                              handleDrop(e, day, slot.hour, slot.minute, resource.id)
                            }
                            className="calendar-time-slot"
                          />
                        )
                      })}

                      {/* Events */}
                      {eventLayouts.map((layout) => {
                        const style = getOverlapEventStyle(layout)
                        const isSelected = selectedEventIds.includes(layout.event.id)

                        return (
                          <div
                            key={layout.event.id}
                            style={{
                              position: 'absolute',
                              top: style.top + 1,
                              left: style.left,
                              width: style.width,
                              height: style.height - 2,
                              zIndex: isSelected ? 2 : 1,
                              paddingLeft: '2px',
                              paddingRight: layout.totalColumns > 1 ? '1px' : '2px',
                            }}
                            onClick={(e) => {
                              const modifiers = {
                                ctrl: e.ctrlKey || e.metaKey,
                                shift: e.shiftKey,
                              }
                              onEventClick?.(layout.event, modifiers)
                            }}
                          >
                            <AppointmentCard
                              event={layout.event}
                              locale={locale}
                              compact={true}
                              draggable={editable}
                              selected={isSelected}
                              onDragStart={handleDragStart(layout.event)}
                            />
                          </div>
                        )
                      })}

                      {dayIsToday && (
                        <CurrentTimeIndicator
                          startHour={startHour}
                          slotDuration={slotDuration}
                          slotHeight={slotHeight}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Group by Date: Days are primary columns, resources are sub-columns
  return (
    <div className="calendar-resource-week-view">
      {/* Headers */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
        }}
      >
        {/* Empty corner */}
        <div style={{ width: '60px', flexShrink: 0 }} />

        {/* Day headers */}
        {weekDays.map((day) => {
          const dayIsToday = isToday(day)

          return (
            <div
              key={day.toISOString()}
              style={{
                flex: 1,
                borderLeft: '1px solid #e5e7eb',
                backgroundColor: dayIsToday ? '#fef3c7' : undefined,
              }}
            >
              {/* Day name */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '8px',
                  borderBottom: '1px solid #e5e7eb',
                  fontWeight: 600,
                }}
              >
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  {getDayNameShort(day.getDay(), locale)}
                </div>
                <div style={{ fontSize: '16px', marginTop: '2px' }}>
                  {day.getDate()}
                </div>
              </div>

              {/* Resource sub-headers */}
              <div style={{ display: 'flex' }}>
                {resources.map((resource) => (
                  <div
                    key={resource.id}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '6px 2px',
                      borderLeft: '1px dashed #e5e7eb',
                      fontSize: '10px',
                      color: '#6b7280',
                    }}
                  >
                    {resource.name}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Grid */}
      <div
        style={{
          display: 'flex',
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'auto',
        }}
      >
        <TimeLabels
          startHour={startHour}
          endHour={endHour}
          slotDuration={slotDuration}
          slotHeight={slotHeight}
        />

        {/* Day columns */}
        <div style={{ display: 'flex', flex: 1 }}>
          {weekDays.map((day) => {
            const dayIsToday = isToday(day)

            return (
              <div
                key={day.toISOString()}
                style={{
                  flex: 1,
                  display: 'flex',
                  borderLeft: '1px solid #e5e7eb',
                }}
              >
                {/* Resource columns within day */}
                {resources.map((resource) => {
                  const dayEvents = getEventsForDay(events, day).filter(
                    (e) => e.doctor.id === resource.id
                  )
                  const eventLayouts = useMemo(
                    () =>
                      calculateOverlapLayout(dayEvents, startHour, slotDuration, slotHeight),
                    [dayEvents]
                  )

                  return (
                    <div
                      key={resource.id}
                      style={{
                        flex: 1,
                        position: 'relative',
                        borderLeft: '1px dashed #e5e7eb',
                        backgroundColor: dayIsToday ? '#fffef0' : undefined,
                        minHeight: gridHeight,
                      }}
                    >
                      {/* Slots */}
                      {slots.map((slot) => {
                        const isHourStart = slot.minute === 0

                        return (
                          <div
                            key={`${slot.hour}-${slot.minute}`}
                            style={{
                              height: slotHeight,
                              borderBottom: isHourStart
                                ? '1px solid #d1d5db'
                                : '1px dashed #e5e7eb',
                              cursor: editable ? 'pointer' : 'default',
                              boxSizing: 'border-box',
                            }}
                            onClick={() =>
                              handleSlotClick(day, slot.hour, slot.minute, resource.id)
                            }
                            onDragOver={handleDragOver}
                            onDrop={(e) =>
                              handleDrop(e, day, slot.hour, slot.minute, resource.id)
                            }
                            className="calendar-time-slot"
                          />
                        )
                      })}

                      {/* Events */}
                      {eventLayouts.map((layout) => {
                        const style = getOverlapEventStyle(layout)
                        const isSelected = selectedEventIds.includes(layout.event.id)

                        return (
                          <div
                            key={layout.event.id}
                            style={{
                              position: 'absolute',
                              top: style.top + 1,
                              left: style.left,
                              width: style.width,
                              height: style.height - 2,
                              zIndex: isSelected ? 2 : 1,
                              paddingLeft: '2px',
                              paddingRight: layout.totalColumns > 1 ? '1px' : '2px',
                            }}
                            onClick={(e) => {
                              const modifiers = {
                                ctrl: e.ctrlKey || e.metaKey,
                                shift: e.shiftKey,
                              }
                              onEventClick?.(layout.event, modifiers)
                            }}
                          >
                            <AppointmentCard
                              event={layout.event}
                              locale={locale}
                              compact={true}
                              draggable={editable}
                              selected={isSelected}
                              onDragStart={handleDragStart(layout.event)}
                            />
                          </div>
                        )
                      })}

                      {dayIsToday && (
                        <CurrentTimeIndicator
                          startHour={startHour}
                          slotDuration={slotDuration}
                          slotHeight={slotHeight}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
